import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["fileInput", "previewArea", "photoGrid"]

  connect() {
    // Try to set up immediately, but fall back to retry if needed
    this.attemptSetup()
  }

  attemptSetup() {
    const uploadArea = this.element.querySelector('.photo-upload-area')

    if (uploadArea) {
      // DOM is ready, set up mobile optimizations
      this.setupMobileOptimizations()
    } else {
      // DOM not ready yet, retry after a short delay
      setTimeout(() => {
        this.attemptSetup()
      }, 50)
    }
  }

  disconnect() {
    // No cleanup needed for drag and drop
  }

  openFileInput() {
    this.fileInputTarget.click()
  }

  handleFiles(event) {
    const files = Array.from(event.target.files)
    this.processFiles(files)
  }

  processFiles(files) {
    const validFiles = files.filter(file => this.validateFile(file))

    if (validFiles.length > 0) {
      this.showPreviewArea()
      validFiles.forEach(file => this.createPhotoPreview(file))

      // Update the file input to include all valid files
      this.updateFileInput(validFiles)
    }
  }

  updateFileInput(files) {
    // Create a new FileList-like object and update the input
    const dataTransfer = new DataTransfer()
    files.forEach(file => dataTransfer.items.add(file))
    this.fileInputTarget.files = dataTransfer.files
  }

  validateFile(file) {
    // Check file type
    if (!file.type.startsWith('image/')) {
      this.showError(`${file.name} is not an image file`)
      return false
    }

    // Check file size (10MB limit)
    const maxSize = 10 * 1024 * 1024 // 10MB
    if (file.size > maxSize) {
      this.showError(`${file.name} is too large. Maximum size is 10MB`)
      return false
    }

    return true
  }

  createPhotoPreview(file) {
    const reader = new FileReader()

    reader.onload = (e) => {
      const photoElement = this.buildPhotoElement(file, e.target.result)
      this.photoGridTarget.appendChild(photoElement)
    }

    reader.readAsDataURL(file)
  }

  buildPhotoElement(file, dataUrl) {
    const photoDiv = document.createElement('div')
    photoDiv.className = 'photo-item relative group'

    photoDiv.innerHTML = `
      <div class="relative overflow-hidden rounded-lg border border-gray-200 hover:shadow-md transition-all duration-200">
        <img src="${dataUrl}" alt="${file.name}" class="w-full h-16 sm:h-20 md:h-24 object-contain hover:scale-105 transition-transform duration-200">
        <div class="absolute inset-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center">
          <button type="button" class="remove-photo-btn opacity-0 group-hover:opacity-100 bg-red-500 text-white rounded-full p-1 sm:p-1.5 hover:bg-red-600 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2" data-action="click->photo-upload#removePhoto">
            <svg class="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      </div>
      <p class="text-xs text-gray-600 mt-2 truncate text-center" title="${file.name}">${file.name}</p>
    `

    return photoDiv
  }

  removePhoto(event) {
    const photoItem = event.target.closest('.photo-item')
    if (photoItem) {
      photoItem.remove()

      // Hide preview area if no photos left
      if (this.photoGridTarget.children.length === 0) {
        this.hidePreviewArea()
      }
    }
  }

  showPreviewArea() {
    this.previewAreaTarget.classList.remove('hidden')
  }

  hidePreviewArea() {
    this.previewAreaTarget.classList.add('hidden')
  }

  setupMobileOptimizations() {
    // Add touch-friendly interactions for mobile
    const uploadArea = this.element.querySelector('.photo-upload-area')

    if (!uploadArea) {
      console.warn('Photo upload area not found, skipping mobile optimizations')
      return
    }

    // Prevent zoom on double tap for mobile
    uploadArea.addEventListener('touchend', (e) => {
      e.preventDefault()
    })

    // Add haptic feedback for mobile devices
    if ('vibrate' in navigator) {
      uploadArea.addEventListener('click', () => {
        navigator.vibrate(50)
      })
    }
  }

  showError(message) {
    // Create a temporary error message
    const errorDiv = document.createElement('div')
    errorDiv.className = 'fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 max-w-sm transform transition-all duration-300 ease-in-out'
    errorDiv.innerHTML = `
      <div class="flex items-center">
        <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
        </svg>
        <span>${message}</span>
      </div>
    `

    document.body.appendChild(errorDiv)

    // Animate in
    setTimeout(() => {
      errorDiv.classList.add('translate-x-0')
    }, 10)

    // Remove after 3 seconds
    setTimeout(() => {
      errorDiv.classList.add('translate-x-full', 'opacity-0')
      setTimeout(() => {
        errorDiv.remove()
      }, 300)
    }, 3000)
  }
}
